import ChoiceField from '@/components/ui/ChoiceField';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import React, { useState, useEffect, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import { Upload, X, Search, Filter, FileText, Image, File, CheckCircle2, AlertCircle, Clock, Trash2, FolderOpen, Layers, ClipboardList, ArrowRight } from 'lucide-react';
import { suggestTags, ALL_TAGS } from '@/lib/tagSuggestions';
import { useOrganisation } from '@/lib/OrganisationContext';
import DocumentViewer from '@/components/evidence/DocumentViewer';
import AskDaiIcon from '@/components/askdai/AskDaiIcon';
import { useAuth } from '@/lib/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const REQUIREMENTS = [
  { id: 'l1_commitments', label: 'Level 1 Core Commitments', level: 1 },
  { id: 'l1_activity', label: 'Level 1 Practical Activity', level: 1 },
  { id: 'l2_recruitment', label: 'Level 2 Getting the Right People', level: 2 },
  { id: 'l2_retention', label: 'Level 2 Keeping & Developing People', level: 2 },
  { id: 'l2_assessment', label: 'Level 2 Self-Assessment', level: 2 },
  { id: 'l3_leadership', label: 'Level 3 Leadership & Strategy', level: 3 },
  { id: 'l3_external', label: 'Level 3 External Influence', level: 3 },
  { id: 'l3_culture', label: 'Level 3 Cultural Maturity', level: 3 },
];

function fileIcon(fileType) {
  if (!fileType) return File;
  if (fileType.includes('image') || fileType.includes('png') || fileType.includes('jpg')) return Image;
  if (fileType.includes('pdf') || fileType.includes('word') || fileType.includes('doc') || fileType.includes('text')) return FileText;
  return File;
}

function TagPill({ tag, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 bg-teal-50 text-teal-700 border border-teal-200 text-xs font-medium px-2 py-0.5 rounded-full">
      {tag}
      {onRemove && (
        <button onClick={() => onRemove(tag)} aria-label={`Remove tag: ${tag}`} className="hover:text-teal-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488] rounded-full">
          <X className="w-3 h-3" aria-hidden="true" />
        </button>
      )}
    </span>
  );
}

function UploadModal({ onClose, onUploaded, onExistingFound, organisationId, tasks, returnFocusRef }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [linkedReq, setLinkedReq] = useState('');
  const [linkedLevel, setLinkedLevel] = useState('');
  const [linkedTaskIds, setLinkedTaskIds] = useState([]);
  const [notes, setNotes] = useState('');
  const [reviewDate, setReviewDate] = useState('');
  const [reviewOwnerEmail, setReviewOwnerEmail] = useState('');
  const [error, setError] = useState('');
  const [existingDocument, setExistingDocument] = useState(null);
  const abortRef = useRef(null);
  const submittingRef = useRef(false);
  const requestIdRef = useRef(crypto.randomUUID());

  useEffect(() => () => abortRef.current?.abort(), []);

  function handleFileChange(event) {
    const next = event.target.files?.[0] || null;
    setFile(next);
    const suggestions = next ? suggestTags(next.name, next.type) : [];
    setSuggestedTags(suggestions);
    setSelectedTags(suggestions);
    setError('');
  }

  function toggleTag(tag) {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(item => item !== tag) : [...prev, tag]);
  }

  async function handleUpload(event) {
    event.preventDefault();
    if (submittingRef.current) return;
    if (!file) { setError('Choose a file to upload.'); return; }
    if (reviewOwnerEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reviewOwnerEmail)) { setError('Enter a valid review owner email address.'); return; }
    submittingRef.current = true; setUploading(true); setError('');
    const controller = new AbortController(); abortRef.current = controller;
    try {
      const result = await base44.integrations.Core.UploadEvidence({
        file, request_id: requestIdRef.current, task_ids: linkedTaskIds, tags: selectedTags, suggested_tags: suggestedTags,
        linked_level: linkedLevel, linked_requirement: linkedReq, notes,
        review_date: reviewDate, review_owner_email: reviewOwnerEmail,
      }, { signal: controller.signal });
      if (result.document?.organisation_id !== organisationId) return;
      onUploaded(result.document);
    } catch (err) {
      if (!controller.signal.aborted && err?.status === 409 && err?.code === 'upload_request_conflict') {
        try {
          const existing = await base44.entities.EvidenceDocument.get(requestIdRef.current);
          if (controller.signal.aborted) return;
          if (existing?.organisation_id === organisationId) {
            setExistingDocument(existing);
            onExistingFound(existing);
            setError(`The original upload may already have been saved as ${existing.name}. Review it in the vault, or start a new upload for your changed details.`);
          } else setError('This upload reference cannot be reused. Start a new upload to save the changed details.');
        } catch { if (!controller.signal.aborted) setError('This upload reference cannot be reused. Start a new upload to save the changed details.'); }
      } else if (!controller.signal.aborted) setError(err?.message || 'Upload failed. Your selections have been kept so you can try again.');
    } finally {
      submittingRef.current = false;
      if (!controller.signal.aborted) setUploading(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !uploading) onClose(); }}>
      <DialogContent returnFocusRef={returnFocusRef} className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload evidence</DialogTitle>
          <DialogDescription>Select one file up to 50 MB. Any file type is accepted and stored privately for this organisation.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleUpload} className="space-y-5" noValidate>
          <div>
            <label htmlFor="file-upload" className="text-sm font-medium text-foreground">Document</label>
            <input id="file-upload" type="file" onChange={handleFileChange} disabled={uploading} className="mt-2 block min-h-11 w-full rounded-lg border border-input bg-background p-2 text-sm text-foreground file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-2 file:font-semibold file:text-primary-foreground" aria-describedby="file-upload-help file-upload-selected" />
            <p id="file-upload-help" className="mt-1 text-xs text-muted-foreground">Activate Choose file or Browse to open your device’s file picker. Select a document, then return here to finish the upload. Maximum file size: 50 MB.</p>
            <p id="file-upload-selected" className="mt-1 text-sm text-foreground" aria-live="polite">{file ? `Selected: ${file.name} (${Math.max(1, Math.round(file.size / 1024))} KB). Ready to upload. Add any links below, then activate Upload document to save this file.` : 'No file selected.'}</p>
          </div>
          {suggestedTags.length > 0 && <fieldset><legend className="text-sm font-medium text-foreground">Document tags</legend><div className="mt-2 flex flex-wrap gap-2">{ALL_TAGS.map(tag => <button type="button" key={tag} onClick={() => toggleTag(tag)} aria-pressed={selectedTags.includes(tag)} disabled={uploading} className={`min-h-11 rounded-full border px-3 py-2 text-xs font-medium ${selectedTags.includes(tag) ? 'border-primary bg-accent text-accent-foreground' : 'border-border bg-background text-muted-foreground'}`}>{tag}</button>)}</div></fieldset>}
          <div className="grid gap-4 sm:grid-cols-2">
            <ChoiceField label="Link to requirement" value={linkedReq} disabled={uploading} options={[["", "None"], ...REQUIREMENTS.map(item => [item.id, item.label])]} onChange={value => { setLinkedReq(value); const requirement = REQUIREMENTS.find(item => item.id === value); if (requirement) setLinkedLevel(String(requirement.level)); }} />
            <ChoiceField label="Level" value={linkedLevel} disabled={uploading} options={[["", "Any"], ["1", "Level 1"], ["2", "Level 2"], ["3", "Level 3"]]} onChange={setLinkedLevel} />
          </div>
          <fieldset><legend className="text-sm font-medium text-foreground">Link to roadmap tasks <span className="font-normal text-muted-foreground">(optional)</span></legend>{tasks.length ? <div className="mt-2 max-h-36 space-y-1 overflow-y-auto rounded-lg border border-border p-2" tabIndex={0}>{tasks.map(task => <label key={task.id} className="flex min-h-11 items-center gap-3 rounded-md px-2 text-sm hover:bg-accent"><input type="checkbox" checked={linkedTaskIds.includes(task.id)} disabled={uploading} onChange={event => setLinkedTaskIds(prev => event.target.checked ? [...prev, task.id] : prev.filter(id => id !== task.id))} className="h-5 w-5" />{task.title}</label>)}</div> : <p className="mt-1 text-sm text-muted-foreground">No roadmap tasks are available to link.</p>}</fieldset>
          <label className="block text-sm font-medium text-foreground">Notes <span className="font-normal text-muted-foreground">(optional)</span><textarea rows={2} value={notes} disabled={uploading} onChange={event => setNotes(event.target.value)} className="mt-1 w-full rounded-lg border border-input bg-background p-3" /></label>
          <div className="grid gap-4 sm:grid-cols-2"><label className="text-sm font-medium text-foreground">Review date <span className="font-normal text-muted-foreground">(optional)</span><input type="date" value={reviewDate} disabled={uploading} onChange={event => setReviewDate(event.target.value)} className="mt-1 min-h-11 w-full rounded-lg border border-input bg-background px-3" /></label><label className="text-sm font-medium text-foreground">Review owner email <span className="font-normal text-muted-foreground">(optional)</span><input type="email" value={reviewOwnerEmail} disabled={uploading} onChange={event => setReviewOwnerEmail(event.target.value)} className="mt-1 min-h-11 w-full rounded-lg border border-input bg-background px-3" /></label></div>
          {error && <p role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}
          {existingDocument && <div className="rounded-lg border border-border bg-muted p-3 text-sm"><a href={existingDocument.file_url} target="_blank" rel="noopener noreferrer" className="font-semibold underline">Open saved {existingDocument.name}</a><button type="button" onClick={() => { requestIdRef.current = crypto.randomUUID(); setExistingDocument(null); setError(''); }} className="mt-3 block min-h-11 rounded-lg border border-border bg-background px-3 py-2 font-semibold">Start a new upload</button></div>}
          {uploading && <p role="status" aria-live="polite" className="text-sm text-muted-foreground">Uploading {file?.name}…</p>}
          <DialogFooter><button type="button" onClick={onClose} disabled={uploading} className="min-h-11 rounded-lg border border-border px-4 font-semibold">Cancel</button><button type="submit" disabled={uploading} className="min-h-11 rounded-lg bg-primary px-4 font-semibold text-primary-foreground disabled:opacity-60">{uploading ? 'Uploading…' : 'Upload document'}</button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function EvidenceVault() {
  const { selectedOrg } = useOrganisation();
  const { user } = useAuth();
  const selectedMembership = user?.organisations?.find(item => item.id === selectedOrg?.id);
  const canUpload = Boolean(selectedOrg?.id && (user?.is_provider || selectedMembership || selectedOrg.id === user?.organisation_id));
  const uploadButtonRef = useRef(null);
  const loadGeneration = useRef(0);
  const [docs, setDocs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const loadController = useRef(null);
  const loadFailed = useRef(false);
  const [showUpload, setShowUpload] = useState(false);
  const [search, setSearch] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [groupBy, setGroupBy] = useState('none'); // 'none' | 'level' | 'category'
  const [view, setView] = useState('vault'); // 'vault' | 'audit' | 'checklist'
  const [currentLevel, setCurrentLevel] = useState(0);
  const [liveMsg, setLiveMsg] = useState('');
  const [viewDoc, setViewDoc] = useState(null);
  const [deletePrompt, setDeletePrompt] = useState(null);
  const deleteReturnRef = useRef(null);
  const activeOrganisationRef = useRef(selectedOrg?.id);
  activeOrganisationRef.current = selectedOrg?.id;
  const [showAskDaiOnly, setShowAskDaiOnly] = useState(false);
  const [analysisDoc, setAnalysisDoc] = useState(null);
  const [analysisKind, setAnalysisKind] = useState('job_description');
  const [analysisNote, setAnalysisNote] = useState('');
  const [analysisBusy, setAnalysisBusy] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisError, setAnalysisError] = useState('');
  const [previousAnalyses, setPreviousAnalyses] = useState([]);
  const [previousAnalysesLoading, setPreviousAnalysesLoading] = useState(false);

  useEffect(() => { setShowUpload(false); setDeletePrompt(null); loadDocs(); }, [selectedOrg]);

  // Realtime: refresh when documents are created/updated/deleted (e.g. by Dai)
  useEffect(() => {
    const unsub = base44.entities.EvidenceDocument.subscribe(event => {
      // A slow traversal must be allowed to finish or reach its deadline.
      if (event.type === 'poll' && (loadController.current || loadFailed.current)) return;
      loadDocs();
    });
    return () => { loadGeneration.current += 1; loadController.current?.abort(); unsub(); };
  }, [selectedOrg]);

  async function loadDocs() {
    const generation = ++loadGeneration.current;
    loadController.current?.abort();
    const controller = new AbortController(); loadController.current = controller;
    const orgId = selectedOrg?.id;
    loadFailed.current = false;
    setLoading(true); setLoadError(false);
    setDocs([]); setTasks([]);
    if (!orgId) { setLoading(false); loadController.current = null; return; }
    try {
      const [list, assessments, orgs, roadmapTasks] = await Promise.all([
        base44.entities.EvidenceDocument.filter(orgId ? { organisation_id: orgId } : {}, '-created_date', 200),
        base44.entities.Assessment.filter(orgId ? { organisation_id: orgId } : {}, '-created_date', 1),
        base44.entities.Organisation.get(orgId),
        base44.roadmapTasks.listAll(orgId, { signal: controller.signal }),
      ]);
      if (generation !== loadGeneration.current || orgId !== selectedOrg?.id) return;
      setDocs(list); setTasks(roadmapTasks);
      const latestAssessment = assessments[0];
      const org = orgs;
      setCurrentLevel(latestAssessment?.current_level || org?.current_dc_level || 0);
    } catch {
      if (generation === loadGeneration.current && !controller.signal.aborted) {
        loadFailed.current = true;
        setLoadError(true);
      }
    } finally {
      if (generation === loadGeneration.current && !controller.signal.aborted) setLoading(false);
      controller.abort(); // Also stop task pages if another member of Promise.all failed.
      if (loadController.current === controller) loadController.current = null;
    }
  }

  function handleUploaded(doc) {
    setDocs(prev => [doc, ...prev]);
    setShowUpload(false);
    setLiveMsg(`Document uploaded successfully: ${doc.name}`);
  }

  function handleExistingFound(doc) {
    setDocs(prev => prev.some(item => item.id === doc.id) ? prev : [doc, ...prev]);
  }

  async function handleDelete(id, name) {
    const orgId = activeOrganisationRef.current;
    await base44.entities.EvidenceDocument.delete(id);
    if (activeOrganisationRef.current !== orgId) return;
    setDocs(prev => prev.filter(d => d.id !== id));
    setLiveMsg(`${name} was removed from the evidence vault.`);
    deleteReturnRef.current = uploadButtonRef.current;
  }

  async function runAnalysis() {
    setAnalysisBusy(true); setAnalysisError('');
    try {
      const response = await base44.tenant.request('POST', '/ai/analyse', { evidence_id: analysisDoc.id, kind: analysisKind, ...(analysisNote ? { note: analysisNote } : {}) });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Analysis failed');
      setAnalysisResult(body); setPreviousAnalyses(prev => [body, ...prev.filter(item => item.id !== body.id)]); setAnalysisBusy(false);
    } catch (error) { setAnalysisError(error.message); setAnalysisBusy(false); }
  }

  async function openAnalysis(doc) {
    setAnalysisDoc(doc); setAnalysisResult(null); setAnalysisError(''); setPreviousAnalyses([]); setPreviousAnalysesLoading(true);
    try {
      const response = await base44.tenant.request('GET', `/ai/analyse?evidence_id=${encodeURIComponent(doc.id)}`);
      const body = await response.json();
      if (response.ok) setPreviousAnalyses(Array.isArray(body) ? body : []);
    } catch { /* Previous results are optional; the new analysis remains available. */ }
    finally { setPreviousAnalysesLoading(false); }
  }

  const filtered = docs.filter(d => {
    const matchSearch = !search || d.name?.toLowerCase().includes(search.toLowerCase()) || d.tags?.some(t => t.toLowerCase().includes(search.toLowerCase()));
    const matchTag = !filterTag || d.tags?.includes(filterTag);
    const matchLevel = !filterLevel || String(d.linked_level) === filterLevel;
    const matchCategory = !filterCategory || d.linked_requirement === filterCategory;
    const matchAskDai = !showAskDaiOnly || d.tags?.includes('Ask Dai');
    return matchSearch && matchTag && matchLevel && matchCategory && matchAskDai;
  });

  // Group documents for structured view
  const groupedDocs = groupBy === 'none' ? { all: filtered } : 
    groupBy === 'level' ? {
      'Level 1': filtered.filter(d => d.linked_level === 1),
      'Level 2': filtered.filter(d => d.linked_level === 2),
      'Level 3': filtered.filter(d => d.linked_level === 3),
      'Unassigned': filtered.filter(d => !d.linked_level),
    } :
    groupBy === 'category' ? REQUIREMENTS.reduce((acc, req) => {
      acc[req.label] = filtered.filter(d => d.linked_requirement === req.id);
      return acc;
    }, { 'Unassigned': filtered.filter(d => !d.linked_requirement) }) : { all: filtered };

  // Audit readiness: which requirements have evidence?
  const auditStatus = REQUIREMENTS.map(req => {
    const evidenceDocs = docs.filter(d => d.linked_requirement === req.id || d.tags?.some(t => req.label.toLowerCase().includes(t.toLowerCase())));
    return { ...req, docs: evidenceDocs, status: evidenceDocs.length >= 2 ? 'complete' : evidenceDocs.length === 1 ? 'partial' : 'missing' };
  });

  // Checklist for next level
  const nextLevel = Math.min(currentLevel + 1, 3);
  const nextLevelReqs = REQUIREMENTS.filter(r => r.level === nextLevel);
  const checklistData = nextLevelReqs.map(req => {
    const evidenceDocs = docs.filter(d => d.linked_requirement === req.id);
    return {
      ...req,
      docs: evidenceDocs,
      isComplete: evidenceDocs.length >= 2,
      isPartial: evidenceDocs.length === 1,
      isMissing: evidenceDocs.length === 0,
    };
  });
  const checklistProgress = nextLevelReqs.length > 0 
    ? Math.round((checklistData.filter(r => r.isComplete).length / nextLevelReqs.length) * 100)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" role="status" aria-live="polite">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-[#0D9488] rounded-full animate-spin" aria-hidden="true"></div>
        <span className="sr-only">Loading evidence vault…</span>
      </div>
    );
  }

  if (loadError) return <div className="rounded-lg border border-border bg-card p-6 text-foreground">
    <p role="alert">The evidence vault could not be loaded. Please try again.</p>
    <button type="button" onClick={loadDocs} className="mt-4 min-h-11 rounded-lg border border-border px-4 font-semibold">Retry loading</button>
  </div>;

  return (
    <div className="evidence-vault-page p-4 sm:p-8 max-w-6xl mx-auto">
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">{liveMsg}</div>

      <header className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="evidence-on-shell text-3xl font-bold text-[#0F1C2E]">Evidence Vault</h1>
          <p className="evidence-on-shell text-slate-500 mt-1">{docs.length} document{docs.length !== 1 ? 's' : ''} stored</p>
        </div>
        {canUpload ? <button
          ref={uploadButtonRef}
          onClick={() => setShowUpload(true)}
          className="evidence-primary flex items-center gap-2 px-5 py-2.5 bg-teal-700 text-white rounded-xl font-semibold text-sm hover:bg-teal-800 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488] focus-visible:outline-offset-2"
        >
          <Upload className="w-4 h-4" aria-hidden="true" /> Upload Evidence
        </button> : <p className="text-sm text-muted-foreground">You have read-only access to evidence.</p>}
      </header>

      {/* View toggle */}
      <div className="flex flex-wrap gap-2 mb-6" role="tablist" aria-label="Evidence view">
        <button role="tab" aria-selected={view === 'vault'} onClick={() => setView('vault')} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488] ${view === 'vault' ? 'bg-[#0F1C2E] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
          Document Vault
        </button>
        <button role="tab" aria-selected={view === 'audit'} onClick={() => setView('audit')} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488] ${view === 'audit' ? 'bg-[#0F1C2E] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
          Audit Readiness
        </button>
        <button role="tab" aria-selected={view === 'checklist'} onClick={() => setView('checklist')} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488] ${view === 'checklist' ? 'bg-[#0F1C2E] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
          <ClipboardList className="w-4 h-4 inline mr-1" aria-hidden="true" />
          Level {nextLevel} Checklist
        </button>
      </div>

      {/* Ask Dai AI folder */}
      {view === 'vault' && (
        <div className="mb-6">
          <button
            onClick={() => setShowAskDaiOnly(!showAskDaiOnly)}
            aria-pressed={showAskDaiOnly}
            className={`evidence-folder flex items-center gap-3 w-full p-4 rounded-xl border-2 transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488] ${
              showAskDaiOnly ? 'border-[#0D9488] bg-teal-50' : 'border-slate-200 bg-white hover:border-[#0D9488] hover:bg-teal-50'
            }`}
          >
            <AskDaiIcon className="w-10 h-10 shrink-0" />
            <div className="flex-1 text-left">
              <p className="text-sm font-bold text-[#0F1C2E]">Ask Dai</p>
              <p className="text-xs text-slate-500">AI-generated documents</p>
            </div>
            <span className="evidence-primary text-sm font-bold px-3 py-1 rounded-full bg-teal-700 text-white">
              {docs.filter(d => d.tags?.includes('Ask Dai')).length}
            </span>
          </button>
        </div>
      )}

      {view === 'vault' ? (
        <>
          {/* Filters */}
          <div className="evidence-filters space-y-3 mb-6">
            <div className="flex flex-wrap gap-3">
              <div className="relative min-w-0 w-full sm:flex-1 sm:min-w-48">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" aria-hidden="true" />
                <input
                  type="search"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search documents or tags…"
                  aria-label="Search documents"
                  className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]"
                />
              </div>
              <select value={filterLevel} onChange={e => setFilterLevel(e.target.value)} aria-label="Filter by level" className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]">
                <option value="">All Levels</option>
                <option value="1">Level 1</option>
                <option value="2">Level 2</option>
                <option value="3">Level 3</option>
              </select>
              <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} aria-label="Filter by category" className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]">
                <option value="">All Categories</option>
                {REQUIREMENTS.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
              <select value={filterTag} onChange={e => setFilterTag(e.target.value)} aria-label="Filter by tag" className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]">
                <option value="">All Tags</option>
                {ALL_TAGS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            {/* Grouping toggle */}
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">
              <Filter className="w-4 h-4 text-slate-400" aria-hidden="true" />
              <span className="evidence-on-shell text-sm font-medium text-slate-600">Group by:</span>
              <button
                onClick={() => setGroupBy('none')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${groupBy === 'none' ? 'bg-[#0F1C2E] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              >
                List
              </button>
              <button
                onClick={() => setGroupBy('level')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${groupBy === 'level' ? 'bg-[#0F1C2E] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              >
                <Layers className="w-3 h-3 inline mr-1" aria-hidden="true" />
                Level
              </button>
              <button
                onClick={() => setGroupBy('category')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${groupBy === 'category' ? 'bg-[#0F1C2E] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              >
                <FolderOpen className="w-3 h-3 inline mr-1" aria-hidden="true" />
                Category
              </button>
            </div>
          </div>

          {/* Document table */}
          {Object.keys(groupedDocs).every(key => groupedDocs[key].length === 0) ? (
            <div className="evidence-on-shell text-center py-16 text-slate-600">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" aria-hidden="true" />
              <p className="text-lg font-medium">No documents found</p>
              <p className="text-sm mt-1">{canUpload ? 'Upload your first piece of evidence to get started.' : 'No evidence is available to view.'}</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedDocs).map(([groupName, groupDocs]) => (
                groupDocs.length > 0 && (
                  <div key={groupName}>
                    <div className="flex items-center gap-2 mb-3">
                      <h3 className="text-sm font-bold text-[#0F1C2E] uppercase tracking-wide">{groupName}</h3>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{groupDocs.length}</span>
                    </div>
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                      <table className="w-full text-sm" aria-label={`Evidence documents in ${groupName}`}>
                        <thead>
                          <tr className="border-b border-slate-100 bg-slate-50">
                            <th scope="col" className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Document</th>
                            <th scope="col" className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Tags</th>
                            <th scope="col" className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Requirement</th>
                            <th scope="col" className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Uploaded</th>
                            <th scope="col" className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">By</th>
                            <th scope="col" className="px-4 py-3"><span className="sr-only">Actions</span></th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupDocs.map((doc, idx) => {
                            const Icon = fileIcon(doc.file_type);
                            const req = REQUIREMENTS.find(r => r.id === doc.linked_requirement);
                            return (
                              <tr key={doc.id} className={`border-b border-slate-50 hover:bg-slate-50 transition-colors ${idx === groupDocs.length - 1 ? 'border-0' : ''}`}>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" aria-hidden="true" />
                                    {doc.file_url ? (
                                      <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="font-medium text-[#0F1C2E] hover:text-[#0D9488] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488] max-w-[200px] truncate block" aria-label={`Open ${doc.name} (opens in new tab)`}>
                                        {doc.name}
                                      </a>
                                    ) : (
                                      <button onClick={() => setViewDoc(doc)} className="font-medium text-[#0F1C2E] hover:text-[#0D9488] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488] max-w-[200px] truncate block text-left" aria-label={`View ${doc.name}`}>
                                        {doc.name}
                                      </button>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex flex-wrap gap-1">
                                    {doc.tags?.slice(0, 3).map(tag => <TagPill key={tag} tag={tag} />)}
                                    {doc.tags?.length > 3 && <span className="text-xs text-slate-400">+{doc.tags.length - 3}</span>}
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  {req ? (
                                    <span className="text-xs font-medium text-slate-600">{req.label}</span>
                                  ) : doc.linked_level ? (
                                    <span className="text-xs font-semibold text-slate-500">Level {doc.linked_level}</span>
                                  ) : (
                                    <span className="text-xs text-slate-300">—</span>
                                  )}
                                  {doc.linked_task_ids?.length > 0 && (
                                    <p className="mt-1 text-xs text-muted-foreground">
                                      Tasks: {doc.linked_task_ids.map(id => tasks.find(task => task.id === id)?.title).filter(Boolean).join(', ') || `${doc.linked_task_ids.length} linked`}
                                    </p>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">{doc.created_date ? new Date(doc.created_date).toLocaleDateString('en-GB') : '—'}</td>
                                <td className="px-4 py-3 text-slate-400 text-xs">{doc.uploaded_by_name || '—'}</td>
                                <td className="px-4 py-3">
                                  {['text/plain','text/markdown','text/csv','application/pdf','application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(doc.file_type) && <button onClick={() => openAnalysis(doc)} className="mr-2 rounded-lg border border-teal-200 px-2 py-1 text-xs font-semibold text-teal-700 hover:bg-teal-50">Analyse</button>}
                                  {canUpload && <button onClick={event => { deleteReturnRef.current = event.currentTarget; setDeletePrompt(doc); }} aria-label={`Delete ${doc.name}`} className="p-1.5 rounded-lg text-slate-200 hover:text-red-500 hover:bg-red-50 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-500">
                                    <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                                  </button>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              ))}
            </div>
          )}
        </>
      ) : (
        /* Audit Readiness View */
        <div>
          <p className="text-sm text-slate-500 mb-4">Shows which scheme requirements have supporting evidence in your vault.</p>
          <div className="space-y-3">
            {auditStatus.map(req => (
              <div key={req.id} className={`flex items-center gap-4 p-4 rounded-xl border-2 ${req.status === 'complete' ? 'border-teal-200 bg-teal-50' : req.status === 'partial' ? 'border-amber-200 bg-amber-50' : 'border-red-100 bg-red-50'}`} role="status" aria-label={`${req.label}: ${req.status === 'complete' ? 'sufficient evidence' : req.status === 'partial' ? 'partial evidence' : 'no evidence'}`}>
                <div className="flex-shrink-0">
                  {req.status === 'complete' && <CheckCircle2 className="w-5 h-5 text-teal-600" aria-hidden="true" />}
                  {req.status === 'partial' && <Clock className="w-5 h-5 text-amber-500" aria-hidden="true" />}
                  {req.status === 'missing' && <AlertCircle className="w-5 h-5 text-red-400" aria-hidden="true" />}
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-semibold ${req.status === 'complete' ? 'text-teal-800' : req.status === 'partial' ? 'text-amber-800' : 'text-red-700'}`}>{req.label}</p>
                  <p className={`text-xs mt-0.5 ${req.status === 'complete' ? 'text-teal-600' : req.status === 'partial' ? 'text-amber-600' : 'text-red-500'}`}>
                    {req.docs.length} document{req.docs.length !== 1 ? 's' : ''} — {req.status === 'complete' ? 'Sufficient evidence' : req.status === 'partial' ? 'Partial — add more evidence' : 'No evidence uploaded'}
                  </p>
                </div>
                <span className={`text-xs font-bold px-3 py-1 rounded-full ${req.status === 'complete' ? 'bg-teal-200 text-teal-800' : req.status === 'partial' ? 'bg-amber-200 text-amber-800' : 'bg-red-100 text-red-600'}`}>
                  {req.status === 'complete' ? '✓ Ready' : req.status === 'partial' ? '⚠ Partial' : '✗ Missing'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {view === 'checklist' && (
        <div>
          <div className="mb-6">
            <h2 className="text-lg font-bold text-[#0F1C2E] mb-2">
              {currentLevel === 0 ? 'Level 1 Preparation Checklist' : `Level ${nextLevel} Readiness Checklist`}
            </h2>
            <p className="text-sm text-slate-500 mb-4">
              {currentLevel === 0 
                ? 'Start your Disability Confident journey — upload evidence for each requirement below.'
                : currentLevel >= 3
                ? 'Congratulations! You\'ve reached Level 3 (Leader). Maintain your evidence and continue best practices.'
                : `Track your progress toward Level ${nextLevel} — upload evidence for each requirement to achieve accreditation.`}
            </p>

            {/* Progress bar */}
            {currentLevel < 3 && (
              <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-slate-700">Progress to Level {nextLevel}</span>
                  <span className="text-sm font-bold text-[#0D9488]">{checklistProgress}%</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-3" role="progressbar" aria-valuenow={checklistProgress} aria-valuemin={0} aria-valuemax={100}>
                  <div
                    className="bg-[#0D9488] h-3 rounded-full transition-all"
                    style={{ width: `${checklistProgress}%` }}
                  />
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  {checklistData.filter(r => r.isComplete).length} of {checklistData.length} requirements complete
                  {checklistData.some(r => r.isPartial) && ` • ${checklistData.filter(r => r.isPartial).length} in progress`}
                </p>
              </div>
            )}

            {currentLevel >= 3 ? (
              <div className="text-center py-12 bg-teal-50 rounded-2xl border border-teal-200">
                <CheckCircle2 className="w-16 h-16 text-teal-600 mx-auto mb-4" aria-hidden="true" />
                <h3 className="text-xl font-bold text-teal-800">Level 3 Leader Achieved!</h3>
                <p className="text-sm text-teal-700 mt-2 max-w-md mx-auto">
                  You've demonstrated leadership in disability inclusion. Continue to maintain your evidence and share best practices with others.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {checklistData.map((req, idx) => (
                  <div
                    key={req.id}
                    className={`rounded-xl border-2 p-5 transition-all ${
                      req.isComplete
                        ? 'border-teal-200 bg-teal-50'
                        : req.isPartial
                        ? 'border-amber-200 bg-amber-50'
                        : 'border-slate-200 bg-white'
                    }`}
                    role="status"
                    aria-label={`${req.label}: ${req.isComplete ? 'complete' : req.isPartial ? 'in progress' : 'not started'}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        {req.isComplete && <CheckCircle2 className="w-6 h-6 text-teal-600" aria-hidden="true" />}
                        {req.isPartial && <Clock className="w-6 h-6 text-amber-500" aria-hidden="true" />}
                        {req.isMissing && <div className="w-6 h-6 rounded-full border-2 border-slate-300" aria-hidden="true" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                            req.level === 1 ? 'bg-slate-200 text-slate-700' :
                            req.level === 2 ? 'bg-teal-100 text-teal-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            Level {req.level}
                          </span>
                          <h3 className="text-sm font-bold text-[#0F1C2E]">{req.label}</h3>
                        </div>
                        <p className="text-sm text-slate-600 mb-3">
                          {req.isComplete
                            ? '✓ Sufficient evidence uploaded'
                            : req.isPartial
                            ? `1 document uploaded — add 1 more to complete`
                            : 'No evidence uploaded yet'}
                        </p>

                        {/* Uploaded documents */}
                        {req.docs.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-medium text-slate-500 mb-1.5">Uploaded evidence:</p>
                            <ul className="space-y-1">
                              {req.docs.map(doc => (
                                <li key={doc.id} className="flex items-center gap-2 text-xs">
                                  <FileText className="w-3 h-3 text-slate-400" aria-hidden="true" />
                                  <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="text-[#0D9488] hover:underline">
                                    {doc.name}
                                  </a>
                                  <span className="text-slate-400">• {new Date(doc.created_date).toLocaleDateString('en-GB')}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Action button */}
                        {canUpload && <button
                          onClick={() => {
                            setShowUpload(true);
                            // Pre-select this requirement in the upload modal would require passing state
                          }}
                          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${
                            req.isMissing
                              ? 'bg-[#0D9488] text-white hover:bg-teal-700'
                              : 'bg-white text-[#0D9488] border border-[#0D9488] hover:bg-teal-50'
                          }`}
                        >
                          <Upload className="w-3 h-3" aria-hidden="true" />
                          {req.isMissing ? 'Upload Evidence' : 'Add More Evidence'}
                          <ArrowRight className="w-3 h-3" aria-hidden="true" />
                        </button>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {deletePrompt && <ConfirmDialog title={`Remove ${deletePrompt.name}?`} description="This removes the document from the evidence vault. Check that it is no longer needed before continuing." returnFocusRef={deleteReturnRef} onClose={() => setDeletePrompt(null)} onConfirm={() => handleDelete(deletePrompt.id, deletePrompt.name)} />}
      {showUpload && canUpload && <UploadModal onClose={() => setShowUpload(false)} onUploaded={handleUploaded} onExistingFound={handleExistingFound} organisationId={selectedOrg?.id} tasks={tasks} returnFocusRef={uploadButtonRef} />}
      {viewDoc && <DocumentViewer doc={viewDoc} onClose={() => setViewDoc(null)} />}
      {analysisDoc && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="analysis-title"><div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"><div className="flex items-start justify-between gap-4"><div><h2 id="analysis-title" className="text-xl font-bold text-[#0F1C2E]">Analyse against the framework</h2><p className="mt-1 text-sm text-slate-500">The document text will be sent to the configured AI provider for analysis.</p></div><button onClick={() => setAnalysisDoc(null)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" aria-label="Close analysis"><X className="h-5 w-5" /></button></div>{!analysisResult ? <><label className="mt-5 block text-sm font-semibold text-slate-700">Document kind<select value={analysisKind} onChange={(e) => setAnalysisKind(e.target.value)} className="mt-1 block min-h-11 w-full rounded-lg border border-slate-300 px-3"><option value="job_description">Job description</option><option value="policy">Policy</option><option value="other">Other</option></select></label><label className="mt-4 block text-sm font-semibold text-slate-700">Optional note<textarea value={analysisNote} onChange={(e) => setAnalysisNote(e.target.value)} maxLength={1000} rows={3} className="mt-1 block w-full rounded-lg border border-slate-300 p-3" placeholder="What should the analysis pay particular attention to?" /></label>{analysisError && <p role="alert" className="mt-3 text-sm text-red-700">{analysisError}</p>}<button onClick={runAnalysis} disabled={analysisBusy} className="mt-5 min-h-11 rounded-lg bg-teal-700 px-4 py-2 font-semibold text-white disabled:opacity-50">{analysisBusy ? 'Analysing…' : 'Analyse document'}</button>{previousAnalysesLoading ? <p className="mt-6 text-sm text-slate-500">Loading previous analyses…</p> : previousAnalyses.length > 0 && <section className="mt-6 border-t border-slate-100 pt-4"><h3 className="font-semibold text-slate-800">Previous analyses</h3><div className="mt-2 space-y-2">{previousAnalyses.map(item => <button key={item.id} onClick={() => setAnalysisResult(item)} className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-left text-sm hover:border-teal-300 hover:bg-teal-50"><span className="font-medium">{item.document_kind?.replace('_', ' ') || 'Analysis'}</span><span className="text-xs text-slate-500">{item.created_at ? new Date(item.created_at).toLocaleString('en-GB') : ''}</span></button>)}</div></section>}</> : <div className="mt-5 space-y-4"><p className="rounded-lg bg-teal-50 p-3 font-semibold text-teal-900">Overall: {analysisResult.result?.overall}</p><p>{analysisResult.result?.summary}</p>{analysisResult.result?.strengths?.length > 0 && <section><h3 className="font-semibold">Strengths</h3><ul className="list-disc pl-5">{analysisResult.result.strengths.map((x, i) => <li key={i}>{x}</li>)}</ul></section>}<section><h3 className="font-semibold">Findings</h3><ul className="space-y-3">{analysisResult.result?.findings?.map((f, i) => <li key={i} className="rounded-lg border border-slate-200 p-3"><p className="font-semibold">{f.severity}: {f.issue}</p>{f.quote && <blockquote className="mt-2 border-l-4 border-amber-300 pl-3 text-sm italic">“{f.quote}”</blockquote>}<p className="mt-2 text-sm">{f.recommendation}</p></li>)}</ul></section>{analysisResult.truncated && <p className="text-sm text-amber-700">The document was truncated at the analysis limit.</p>}{analysisResult.quotes_unverified > 0 && <p className="text-sm text-slate-500">{analysisResult.quotes_unverified} quote(s) could not be verified against the source.</p>}</div>}</div></div>}
    </div>
  );
}
